import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import Login from "./components/Login.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import HUD from "./components/HUD.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import Leaderboard from "./pages/Leaderboard.jsx";
import Challenges from "./pages/Challenges.jsx";
import BudgetCommander from "./games/BudgetCommander/BudgetCommander.jsx";
import TimeRaid from "./games/TimeRaid/TimeRaid.jsx";
import CompoundQuest from "./games/CompoundQuest/CompoundQuest.jsx";
import PropertyTycoon from "./games/PropertyTycoon/PropertyTycoon.jsx";
import BondBastion from "./games/BondBastion/BondBastion.jsx";
import WildcatPortfolio from "./games/WildcatPortfolio/WildcatPortfolio.jsx";
import OffTheClock from "./games/OffTheClock/OffTheClock.jsx";
import InnerCitadel from "./games/InnerCitadel/InnerCitadel.jsx";
import SignalBoost from "./games/SignalBoost/SignalBoost.jsx";
import MoatRunner from "./games/MoatRunner/MoatRunner.jsx";
import TheStandoff from "./games/TheStandoff/TheStandoff.jsx";
import BudgetBlitz from "./games/BudgetBlitz/BudgetBlitz.jsx";
import MarketPulse from "./games/MarketPulse/MarketPulse.jsx";
import ScamSpotter from "./games/ScamSpotter/ScamSpotter.jsx";
import CashCascade from "./games/CashCascade/CashCascade.jsx";
import NotFound from "./components/NotFound.jsx";
import Honeypot from "./components/Honeypot.jsx";
import { HONEYPOT_PATHS } from "./lib/honeypotPaths.js";
import { isMusicMuted, startMusic } from "./lib/music.js";

function AppShell({ children }) {
  return (
    <>
      <HUD />
      <main className="min-h-[calc(100vh-64px)]">{children}</main>
    </>
  );
}

export default function App() {
  useEffect(() => {
    if (isMusicMuted()) return undefined;

    // Music was left on from a previous session, but browsers block audio
    // playback before any user gesture on the page — wait for the first
    // click or keypress anywhere, then start it and stop listening.
    const handleFirstGesture = () => {
      startMusic();
      window.removeEventListener("click", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };
    window.addEventListener("click", handleFirstGesture);
    window.addEventListener("keydown", handleFirstGesture);

    return () => {
      window.removeEventListener("click", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };
  }, []);

  return (
    <>
      {/* Signature CRT effect — sits above every route */}
      <div className="crt-overlay" />
      <div className="crt-sweep" />

      <Routes>
        <Route path="/login" element={<Login />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppShell>
                <Dashboard />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <AppShell>
                <Leaderboard />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/challenges"
          element={
            <ProtectedRoute>
              <AppShell>
                <Challenges />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/budget-commander"
          element={
            <ProtectedRoute>
              <AppShell>
                <BudgetCommander />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/time-raid"
          element={
            <ProtectedRoute>
              <AppShell>
                <TimeRaid />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/compound-quest"
          element={
            <ProtectedRoute>
              <AppShell>
                <CompoundQuest />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/property-tycoon"
          element={
            <ProtectedRoute>
              <AppShell>
                <PropertyTycoon />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/bond-bastion"
          element={
            <ProtectedRoute>
              <AppShell>
                <BondBastion />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/wildcat-portfolio"
          element={
            <ProtectedRoute>
              <AppShell>
                <WildcatPortfolio />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/off-the-clock"
          element={
            <ProtectedRoute>
              <AppShell>
                <OffTheClock />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/inner-citadel"
          element={
            <ProtectedRoute>
              <AppShell>
                <InnerCitadel />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/signal-boost"
          element={
            <ProtectedRoute>
              <AppShell>
                <SignalBoost />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/moat-runner"
          element={
            <ProtectedRoute>
              <AppShell>
                <MoatRunner />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/the-standoff"
          element={
            <ProtectedRoute>
              <AppShell>
                <TheStandoff />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/budget-blitz"
          element={
            <ProtectedRoute>
              <AppShell>
                <BudgetBlitz />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/market-pulse"
          element={
            <ProtectedRoute>
              <AppShell>
                <MarketPulse />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/scam-spotter"
          element={
            <ProtectedRoute>
              <AppShell>
                <ScamSpotter />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/games/cash-cascade"
          element={
            <ProtectedRoute>
              <AppShell>
                <CashCascade />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {HONEYPOT_PATHS.map((path) => (
          <Route key={path} path={path} element={<Honeypot path={path} />} />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
